import React, { useState, useMemo, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  Sparkles, 
  X, 
  ChevronDown, 
  ChevronRight,
  Clock,
  Calendar,
  Copy,
  FolderX,
  TrendingUp,
  AlertOctagon,
  Filter,
  FilterX,
  Timer,
} from 'lucide-react';
import type { AnomalyWarning, PersonalInsight } from '@/services/aiService';
import type { DayKey } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface AIInsightsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warnings: AnomalyWarning[];
  onDismiss?: (warningId: string) => void;
  onAutoFix?: (warningId: string) => void;
  /**
   * Task 3.9 — Called when the user clicks "Review overtime hours" on a
   * `contracted-hours-exceeded` warning card. Receives the warning's entryId
   * (may be undefined) and the pre-computed per-day suggestions so the caller
   * can open `OvertimeClassificationModal` pre-populated.
   */
  onClassifyOvertime?: (suggestedOvertimeHours: Partial<Record<DayKey, number>>) => void;
  /** When provided, renders a dedicated Overtime Summary card above the anomaly groups */
  overtimeInsight?: PersonalInsight | null;
  /** Warning IDs that cannot be dismissed (blocking policy violations) */
  nonDismissableIds?: Set<string>;
}

// Category definitions with metadata - grouped by policy check alert type
const CATEGORY_CONFIG = {
  'legal-maximum': {
    label: 'Legal Maximum',
    icon: AlertOctagon,
    description: 'Hours exceed legal working time limits',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    priority: 0,
  },
  'excessive-hours': {
    label: 'Policy Violations',
    icon: AlertTriangle,
    description: 'Hours exceed expected working hours',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    priority: 1,
  },
  'missing-hours': {
    label: 'Time Tracking Issues',
    icon: Clock,
    description: 'Fewer hours logged than expected',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    priority: 2,
  },
  'non-working-day': {
    label: 'Non-Working Day',
    icon: Calendar,
    description: 'Hours logged on non-working days',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    priority: 3,
  },
  'data-quality': {
    label: 'Data Quality',
    icon: Copy,
    description: 'Duplicate entries and data inconsistencies',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    priority: 10,
  },
  'patterns': {
    label: 'Patterns & Insights',
    icon: TrendingUp,
    description: 'Unusual patterns and optimization suggestions',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    priority: 11,
  },
  'project-issues': {
    label: 'Project Issues',
    icon: FolderX,
    description: 'Closed projects and project-related warnings',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    priority: 12,
  },
};

// Fallback mapping for non-policy warning types
const TYPE_TO_CATEGORY: Partial<Record<AnomalyWarning['type'], keyof typeof CATEGORY_CONFIG>> = {
  'excessive-hours': 'excessive-hours',
  'missing-time': 'missing-hours',
  'duplicate-entry': 'data-quality',
  'closed-project': 'project-issues',
  'unusual-pattern': 'patterns',
  'time-and-absence-conflict': 'data-quality',
};

// Map a warning to its display category using policyCheckType when available
function getWarningCategory(warning: AnomalyWarning): keyof typeof CATEGORY_CONFIG {
  if (warning.policyCheckType) {
    if (warning.policyCheckType.includes('legal-')) return 'legal-maximum';
    if (warning.policyCheckType.includes('overage')) return 'excessive-hours';
    if (warning.policyCheckType.includes('missing-hours')) return 'missing-hours';
    if (warning.policyCheckType.includes('non-working-day')) return 'non-working-day';
  }
  return TYPE_TO_CATEGORY[warning.type] || 'patterns';
}

// Priority levels
type PriorityLevel = 'high' | 'medium' | 'low';

interface GroupedWarnings {
  category: keyof typeof CATEGORY_CONFIG;
  warnings: AnomalyWarning[];
  highestPriority: PriorityLevel;
}

export const AIInsightsSheet: React.FC<AIInsightsSheetProps> = ({
  open,
  onOpenChange,
  warnings,
  onDismiss,
  onAutoFix,
  onClassifyOvertime,
  overtimeInsight,
  nonDismissableIds,
}) => {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<PriorityLevel>>(
    new Set(['high', 'medium', 'low'])
  );
  const [selectedWarnings, setSelectedWarnings] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [areAllCollapsed, setAreAllCollapsed] = useState(false);

  // Group warnings by alert type category with priority ordering
  const groupedWarnings = useMemo(() => {
    const groups = new Map<keyof typeof CATEGORY_CONFIG, AnomalyWarning[]>();

    warnings.forEach((warning) => {
      const category = getWarningCategory(warning);
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(warning);
    });

    // Convert to array and sort by category priority, then by severity within each group
    const result: GroupedWarnings[] = Array.from(groups.entries()).map(([category, warnings]) => {
      const sortedWarnings = [...warnings].sort((a, b) => {
        const severityOrder = { high: 0, medium: 1, low: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      });

      const highestPriority = sortedWarnings[0]?.severity || 'low';

      return {
        category,
        warnings: sortedWarnings,
        highestPriority,
      };
    });

    // Sort groups by category priority (Legal Max first, then Excessive Hours, etc.)
    result.sort((a, b) => {
      const configA = CATEGORY_CONFIG[a.category];
      const configB = CATEGORY_CONFIG[b.category];
      return configA.priority - configB.priority;
    });

    return result;
  }, [warnings]);

  // Filter by priority
  const filteredGroups = useMemo(() => {
    return groupedWarnings
      .map((group) => ({
        ...group,
        warnings: group.warnings.filter((w) => activeFilters.has(w.severity)),
      }))
      .filter((group) => group.warnings.length > 0);
  }, [groupedWarnings, activeFilters]);

  // Calculate badge counts
  const badgeCounts = useMemo(() => {
    return {
      high: warnings.filter((w) => w.severity === 'high').length,
      medium: warnings.filter((w) => w.severity === 'medium').length,
      low: warnings.filter((w) => w.severity === 'low').length,
    };
  }, [warnings]);

  // Sync isAllSelected state when selections change
  useEffect(() => {
    const visibleWarningIds = filteredGroups.flatMap((group) =>
      group.warnings.map((w) => w.id)
    );
    const allVisible = visibleWarningIds.length > 0 && 
      visibleWarningIds.every((id) => selectedWarnings.has(id));
    setIsAllSelected(allVisible);
  }, [selectedWarnings, filteredGroups]);

  // Sync areAllCollapsed state when categories change
  useEffect(() => {
    const visibleCategories = filteredGroups.map((group) => group.category);
    const allCollapsed = visibleCategories.length > 0 &&
      visibleCategories.every((cat) => collapsedCategories.has(cat));
    setAreAllCollapsed(allCollapsed);
  }, [collapsedCategories, filteredGroups]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleFilter = (priority: PriorityLevel) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(priority)) {
        next.delete(priority);
      } else {
        next.add(priority);
      }
      return next;
    });
  };

  const toggleWarningSelection = (warningId: string) => {
    setSelectedWarnings((prev) => {
      const next = new Set(prev);
      if (next.has(warningId)) {
        next.delete(warningId);
      } else {
        next.add(warningId);
      }
      return next;
    });
  };

  const handleDismissSelected = () => {
    if (onDismiss) {
      selectedWarnings.forEach((warningId) => {
        onDismiss(warningId);
      });
      setSelectedWarnings(new Set());
    }
  };

  const canDismiss = (warningId: string) => !nonDismissableIds?.has(warningId);

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedWarnings(new Set());
      setIsAllSelected(false);
    } else {
      // Select all visible dismissable warnings (filtered)
      const visibleWarningIds = filteredGroups.flatMap((group) =>
        group.warnings.filter((w) => canDismiss(w.id)).map((w) => w.id)
      );
      setSelectedWarnings(new Set(visibleWarningIds));
      setIsAllSelected(true);
    }
  };

  const handleToggleCollapseAll = () => {
    if (areAllCollapsed) {
      // Expand all
      setCollapsedCategories(new Set());
      setAreAllCollapsed(false);
    } else {
      // Collapse all visible categories
      const allCategories = filteredGroups.map((group) => group.category);
      setCollapsedCategories(new Set(allCategories));
      setAreAllCollapsed(true);
    }
  };

  const getSeverityIcon = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4" />;
      case 'low':
        return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: AnomalyWarning['severity']): 'destructive' | 'warning' | 'info' => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
    }
  };

  const getSeverityBadgeColor = (severity: AnomalyWarning['severity']) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full sm:max-w-xl flex flex-col h-full"
      >
        <SheetHeader className="sticky top-0 bg-background z-10 border-b pb-4">
          {/* Row 1: Title */}
          <div className="flex items-center gap-4 flex-wrap">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Insights
            </SheetTitle>
          </div>

          {/* Row 2: Description (hidden on mobile) */}
          <SheetDescription className="hidden sm:block">
            Review and manage detected issues in your timesheet
          </SheetDescription>

          {/* Row 3: Control Buttons */}
          <div className="flex items-center justify-between gap-2 flex-wrap pt-3">
            {/* Left side: Action buttons */}
            <div className="flex items-center gap-2">
              {onDismiss && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleSelectAll}
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleCollapseAll}
              >
                {areAllCollapsed ? 'Expand All' : 'Collapse All'}
              </Button>
            </div>

            {/* Right side: Filter button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  {activeFilters.size === 3 ? (
                    <Filter className="h-4 w-4" />
                  ) : (
                    <FilterX className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">Filter</span>
                  {activeFilters.size < 3 && (
                    <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">
                      {activeFilters.size}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56">
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Filter Priorities</h4>
                  <div className="space-y-2">
                    {badgeCounts.high > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('high')}
                          onCheckedChange={() => toggleFilter('high')}
                        />
                        <span className="text-sm flex-1">High</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                          {badgeCounts.high}
                        </Badge>
                      </label>
                    )}
                    {badgeCounts.medium > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('medium')}
                          onCheckedChange={() => toggleFilter('medium')}
                        />
                        <span className="text-sm flex-1">Medium</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                          {badgeCounts.medium}
                        </Badge>
                      </label>
                    )}
                    {badgeCounts.low > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={activeFilters.has('low')}
                          onCheckedChange={() => toggleFilter('low')}
                        />
                        <span className="text-sm flex-1">Low</span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                          {badgeCounts.low}
                        </Badge>
                      </label>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </SheetHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* Overtime Summary Card — shown to approvers when timesheet has overtime entries */}
          {overtimeInsight && (
            <div className={cn(
              'mb-4 border rounded-lg overflow-hidden',
              overtimeInsight.severity === 'warning' ? 'border-amber-300' : 'border-amber-200'
            )}>
              {/* Card header */}
              <div className={cn(
                'flex items-center gap-3 px-3 py-2.5',
                overtimeInsight.severity === 'warning' ? 'bg-amber-50' : 'bg-amber-50/60'
              )}>
                <Timer className="h-4 w-4 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-amber-800">{overtimeInsight.title}</h4>
                  <p className="text-xs text-amber-700/80">{overtimeInsight.description}</p>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    'shrink-0 text-xs font-semibold',
                    overtimeInsight.severity === 'warning'
                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  )}
                >
                  {overtimeInsight.metric}
                </Badge>
              </div>
              {/* Actionable hint — only shown at warning severity */}
              {overtimeInsight.actionable && (
                <div className="px-3 py-2 bg-white border-t border-amber-200">
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    {overtimeInsight.actionable}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Grouped warnings */}
          <div className="space-y-3">
          {filteredGroups.map((group) => {
            const config = CATEGORY_CONFIG[group.category];
            const isCollapsed = collapsedCategories.has(group.category);
            const CategoryIcon = config.icon;

            return (
              <div
                key={group.category}
                className="border rounded-lg overflow-hidden transition-all border-border"
              >
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(group.category)}
                  className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-muted/50 bg-muted/20"
                >
                  <div className="flex items-center gap-3">
                    <CategoryIcon className="h-4 w-4 text-foreground" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {config.label}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {config.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {group.warnings.length}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn('text-xs', getSeverityBadgeColor(group.highestPriority))}
                    >
                      {group.highestPriority}
                    </Badge>
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Category warnings */}
                {!isCollapsed && (
                  <div className="p-2 space-y-2 bg-background">
                    {group.warnings.map((warning) => (
                      <Alert
                        key={warning.id}
                        variant={getSeverityColor(warning.severity)}
                        className="relative"
                        showIcon={false}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox for selection — only shown when dismiss is available and warning is dismissable */}
                          {onDismiss && canDismiss(warning.id) && (
                            <Checkbox
                              checked={selectedWarnings.has(warning.id)}
                              onCheckedChange={() => toggleWarningSelection(warning.id)}
                              className="mt-0.5"
                            />
                          )}

                          {/* Warning content */}
                          <div className="flex-1 space-y-1 pr-8">
                            <AlertTitle className="text-sm font-medium">
                              {warning.message}
                            </AlertTitle>
                            {warning.suggestion && (
                              <AlertDescription className="text-xs">
                                💡 {warning.suggestion}
                              </AlertDescription>
                            )}

                            {/* Action buttons */}
                            <div className="mt-2 flex flex-wrap gap-2">
                              {warning.autoFixAvailable && onAutoFix && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => onAutoFix(warning.id)}
                                >
                                  Auto-fix
                                </Button>
                              )}
                              {/* Task 3.10 — "Review overtime hours" CTA on contracted-hours-exceeded */}
                              {warning.type === 'contracted-hours-exceeded' && onClassifyOvertime && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                                  onClick={() => {
                                    onClassifyOvertime(warning.suggestedOvertimeHours ?? {});
                                    onOpenChange(false);
                                  }}
                                >
                                  <Timer className="h-3 w-3 mr-1" />
                                  Review overtime hours
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Close button (top-right) — hidden for non-dismissable warnings */}
                          {onDismiss && canDismiss(warning.id) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 absolute top-2 right-2"
                              onClick={() => onDismiss(warning.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </Alert>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty state when all filtered out */}
          {filteredGroups.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No issues match the selected filters</p>
            </div>
          )}
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 bg-background z-10 border-t pt-4">
          {onDismiss && (
            <Button
              variant="outline"
              disabled={selectedWarnings.size === 0}
              onClick={handleDismissSelected}
            >
              Dismiss Selected
              {selectedWarnings.size > 0 && ` (${selectedWarnings.size})`}
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
